import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Dropdown,
  Empty,
  List,
  Typography,
  notification as antNotification,
} from 'antd';
import { BellOutlined, DeleteOutlined } from '@ant-design/icons';

import { getNotificaciones, marcarNotificacionLeida, eliminarNotificacion, eliminarTodasNotificaciones } from '../api';
import type { Notificacion } from '../api';

const { Text } = Typography;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'hace unos segundos';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return 'hace 1 hora';
  if (hrs < 24) return `hace ${hrs} horas`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'hace 1 día';
  return `hace ${days} días`;
}

const TIPO_COLOR: Record<string, string> = {
  VERIFICACION_COMPLETADA: '#52c41a',
  CONCILIACION_EXITOSA: '#1890ff',
  CONCILIACION_ERROR: '#ff4d4f',
};

export function CampanaNotificaciones() {
  const location = useLocation();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [sinLeer, setSinLeer] = useState(0);
  const [open, setOpen] = useState(false);
  const prevIdsRef = useRef<Set<string>>(new Set());
  const firstLoadRef = useRef(true);
  const locationPathnameRef = useRef(location.pathname);
  const navigateRef = useRef(navigate);

  useEffect(() => { locationPathnameRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  const fetchNotificaciones = useCallback(async () => {
    try {
      const res = await getNotificaciones(false);
      setNotificaciones(res.notificaciones);
      setSinLeer(res.sin_leer);

      // Detectar notificaciones nuevas para toast
      if (!firstLoadRef.current) {
        const nuevas = res.notificaciones.filter(n => !prevIdsRef.current.has(n.id) && !n.leida);

        if (nuevas.length > 0 && locationPathnameRef.current !== '/carga') {
          const ultima = nuevas[0];
          antNotification.open({
            message: ultima.titulo,
            description: ultima.mensaje,
            placement: 'topRight',
            onClick: () => {
              antNotification.destroy();
              if (ultima.link) {
                navigateRef.current(ultima.link);
              }
            },
            style: { cursor: 'pointer' },
          });
        }
      }

      firstLoadRef.current = false;
      prevIdsRef.current = new Set(res.notificaciones.map(n => n.id));
    } catch {
      // Silenciar errores de polling
    }
  }, []);

  useEffect(() => {
    fetchNotificaciones();
    const id = setInterval(fetchNotificaciones, 30000);
    return () => clearInterval(id);
  }, [fetchNotificaciones]);

  async function handleMarcarLeida(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const res = await marcarNotificacionLeida(id);
      setSinLeer(res.sin_leer);
      setNotificaciones(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    } catch {
      // ignore
    }
  }

  async function handleEliminar(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const res = await eliminarNotificacion(id);
      setSinLeer(res.sin_leer);
      setNotificaciones(prev => prev.filter(n => n.id !== id));
    } catch {
      // ignore
    }
  }

  async function handleEliminarTodas(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const res = await eliminarTodasNotificaciones();
      setSinLeer(res.sin_leer);
      setNotificaciones([]);
    } catch {
      // ignore
    }
  }

  const dropdownContent = (
    <div style={{
      width: 360,
      maxHeight: 400,
      overflow: 'auto',
      background: '#fff',
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      border: '1px solid #f0f0f0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', borderRadius: '8px 8px 0 0' }}>
        <Text strong>Notificaciones</Text>
        {notificaciones.length > 0 && (
          <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={handleEliminarTodas}>
            Borrar todas
          </Button>
        )}
      </div>

      {notificaciones.length === 0 ? (
        <Empty description="Sin notificaciones" style={{ padding: 24 }} />
      ) : (
        <List
          dataSource={notificaciones}
          renderItem={item => (
            <List.Item
              style={{
                padding: '10px 12px',
                cursor: item.link ? 'pointer' : 'default',
                background: item.leida ? '#fafafa' : '#fff',
                borderLeft: `3px solid ${TIPO_COLOR[item.tipo] || '#d9d9d9'}`,
                opacity: item.leida ? 0.7 : 1,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!item.leida) (e.currentTarget as HTMLElement).style.background = '#f0f5ff';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = item.leida ? '#fafafa' : '#fff';
              }}
              onClick={() => {
                if (item.link) navigate(item.link);
                if (!item.leida) handleMarcarLeida(item.id, {} as React.MouseEvent);
              }}
              actions={[
                <Button
                  key="borrar"
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => handleEliminar(item.id, e)}
                  title="Borrar notificación"
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  <Text strong style={{ fontSize: 13 }}>
                    {item.titulo}
                  </Text>
                }
                description={
                  <div>
                    <Text style={{ fontSize: 12, display: 'block' }}>{item.mensaje}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {timeAgo(item.created_at)}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => dropdownContent}
      placement="bottomRight"
      trigger={['click']}
    >
      <Badge count={sinLeer} size="small" offset={[2, 2]}>
        <BellOutlined style={{ fontSize: 18, cursor: 'pointer', color: '#595959' }} />
      </Badge>
    </Dropdown>
  );
}
